import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Globe } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const humanKey = (k) => k.replace(/^cms_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AdminCmsPage() {
  const [settings, setSettings] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cms-settings");
      setSettings(data.data.settings);
      const v = {};
      data.data.settings.forEach((s) => { v[s.setting_key] = s.setting_value; });
      setValues(v);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      settings.forEach((s) => { payload[s.setting_key] = values[s.setting_key]; });
      await api.put("/admin/cms-settings", { settings: payload });
      toast.success("Website content updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="CMS / Website Content" breadcrumbs={["Platform", "CMS"]}>
        <LoadingBlock label="Loading CMS…" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="CMS / Website Content" breadcrumbs={["Platform", "CMS"]}>
      <div className="max-w-3xl" data-testid={ADMIN.cmsRoot}>
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#101876]/10 text-[#101876]"><Globe className="h-5 w-5" /></div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Public website content</div>
              <h3 className="text-lg font-bold text-slate-900">Edit copy shown on the marketing site</h3>
              <p className="mt-1 text-xs text-slate-500">Copy is stored in platform_settings under the <code className="rounded bg-slate-100 px-1 text-[10px]">cms</code> module. Full page-level CMS lands in a future phase.</p>
            </div>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {settings.map((s) => (
              <div key={s.setting_key} className={s.setting_type === "text" ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{humanKey(s.setting_key)}</Label>
                {s.setting_type === "text" ? (
                  <Textarea rows={3} value={values[s.setting_key] ?? ""} onChange={(e) => setValues((cur) => ({ ...cur, [s.setting_key]: e.target.value }))} data-testid={ADMIN.cmsInput(s.setting_key)} />
                ) : (
                  <Input value={values[s.setting_key] ?? ""} onChange={(e) => setValues((cur) => ({ ...cur, [s.setting_key]: e.target.value }))} data-testid={ADMIN.cmsInput(s.setting_key)} />
                )}
                {s.description && <div className="text-[11px] text-slate-500">{s.description}</div>}
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
              data-testid={ADMIN.cmsSubmit}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save website content
            </button>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
